/**
 * taskflow-ordering.ts
 *
 * Base-36 LexoRank Fractional Indexing Engine for TaskFlow.
 * Enables O(1) card repositioning between any two items without full list recalculation,
 * with deterministic tie-breaking and automatic re-spacing.
 */

export interface OrderedEntity {
  id: string;
  fractionalIndex: string;
}

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const BASE = ALPHABET.length;
const INITIAL_STEP = 10000;
const REBALANCE_LENGTH_THRESHOLD = 16;

export class LexoRankEngine {
  /**
   * Generates initial rank for first item
   */
  static generateInitialRank(): string {
    return 'm00000';
  }

  /**
   * Generate a fractional rank between prevRank and nextRank
   */
  static generateRankBetween(
    prevRank: string | null | undefined,
    nextRank: string | null | undefined,
    entityId?: string
  ): string {
    let baseRank: string;

    if (!prevRank && !nextRank) {
      baseRank = this.generateInitialRank();
    } else if (!prevRank && nextRank) {
      // Prepend before nextRank
      baseRank = this.decrementRank(nextRank);
    } else if (prevRank && !nextRank) {
      // Append after prevRank
      baseRank = this.incrementRank(prevRank);
    } else {
      // Interpolate between prevRank and nextRank
      baseRank = this.midpoint(prevRank!, nextRank!);
    }

    if (entityId) {
      // Deterministic tie-breaker suffix to prevent concurrent duplicate ranks
      const safeSuffix = entityId.replace(/[^a-z0-9]/gi, '').slice(0, 6);
      return `${baseRank}_${safeSuffix}`;
    }

    return baseRank;
  }

  /**
   * Check if rank length has grown excessively and needs rebalancing
   */
  static needsRebalance(rank: string): boolean {
    if (!rank) return false;
    const baseRank = rank.split('_')[0];
    return baseRank.length > REBALANCE_LENGTH_THRESHOLD;
  }

  /**
   * Rebalances a list of items to clean, evenly spaced short ranks while preserving exact order
   */
  static rebalance<T extends OrderedEntity>(items: T[]): T[] {
    const sorted = [...items].sort((a, b) =>
      a.fractionalIndex.localeCompare(b.fractionalIndex)
    );

    return sorted.map((item, index) => {
      const num = (index + 1) * INITIAL_STEP;
      const base36 = num.toString(36).padStart(6, '0');
      return {
        ...item,
        fractionalIndex: `a${base36}`,
      };
    });
  }

  // --- Internal Base-36 Math ---

  private static incrementRank(rank: string): string {
    const base = rank.split('_')[0];
    const lastChar = base[base.length - 1];
    const idx = ALPHABET.indexOf(lastChar);

    if (idx < BASE - 2) {
      return base.slice(0, -1) + ALPHABET[idx + 1];
    }

    return base + 'm';
  }

  private static decrementRank(rank: string): string {
    const base = rank.split('_')[0];
    const firstChar = base[0];
    const idx = ALPHABET.indexOf(firstChar);

    if (idx > 1) {
      return ALPHABET[idx - 1] + base.slice(1);
    }

    return '0' + base;
  }

  private static midpoint(prev: string, next: string): string {
    const pBase = prev.split('_')[0];
    const nBase = next.split('_')[0];

    const maxLen = Math.max(pBase.length, nBase.length);
    const pPad = pBase.padEnd(maxLen + 1, '0');
    const nPad = nBase.padEnd(maxLen + 1, '0');

    let result = '';
    let carry = 0;

    for (let i = 0; i < pPad.length; i++) {
      const pVal = ALPHABET.indexOf(pPad[i]);
      const nVal = ALPHABET.indexOf(nPad[i]);

      const avg = Math.floor((pVal + nVal + carry * BASE) / 2);
      carry = (pVal + nVal + carry * BASE) % 2;

      result += ALPHABET[avg];

      if (result > pBase && result < nBase) {
        break;
      }
    }

    if (result <= pBase || result >= nBase) {
      result = pBase + 'm';
    }

    return result;
  }
}
